/**
 * Discord Rich Presence Hook
 * 
 * React hook for managing Discord Rich Presence updates.
 * Detects game state changes and updates Discord presence accordingly.
 */

import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

// Check if running in Electron
const isElectron = window.location.protocol === 'file:' || window.electron !== undefined;

/**
 * Hook to update Discord Rich Presence
 * @param {string} state - Presence state (e.g., "In Menu", "In Queue", "In Battle")
 * @param {string} details - Optional details text
 * @param {boolean} showElapsedTime - Whether to show elapsed time from now
 */
export function useDiscordPresence(state, details = null, showElapsedTime = false) {
  const location = useLocation();
  const startTimestampRef = useRef(null);

  useEffect(() => {
    // Only run in Electron
    if (!isElectron || !window.electron || !window.electron.discord) {
      return;
    }

    // Set start timestamp if showing elapsed time
    if (showElapsedTime && !startTimestampRef.current) {
      startTimestampRef.current = Math.floor(Date.now() / 1000);
    } else if (!showElapsedTime) {
      startTimestampRef.current = null;
    }

    // Update Discord presence
    if (state) {
      window.electron.discord.updatePresence(
        state,
        details,
        startTimestampRef.current
      );
    }

    // Cleanup: Clear presence when component unmounts or state becomes null
    return () => {
      if (!state && window.electron && window.electron.discord) {
        // Only clear if we're leaving a state (not just updating)
        // This prevents clearing when navigating between routes
      }
    };
  }, [state, details, showElapsedTime]);
}

/**
 * Hook to automatically detect route and update Discord presence
 * This is a convenience hook that updates presence based on current route
 */
export function useDiscordPresenceAuto() {
  const location = useLocation();

  useEffect(() => {
    // Only run in Electron
    if (!isElectron || !window.electron || !window.electron.discord) {
      return;
    }

    const pathname = location.pathname;

    // Determine presence based on route
    let state = null;
    let details = null;

    if (
      pathname === '/battle' ||
      pathname.startsWith('/battle/') ||
      pathname.startsWith('/campaign-battle')
    ) {
      // Battle routes - let BattleUI/CampaignBattleUI handle presence updates
      // Don't override their presence with elapsed time
      return;
    } else if (pathname === '/battle-overview') {
      state = 'In Menu';
    } else if (pathname === '/strategy' || pathname === '/profile' || pathname === '/honor-hall') {
      state = 'In Menu';
    } else {
      // Default: In Menu
      state = 'In Menu';
    }

    // Update presence
    if (state) {
      window.electron.discord.updatePresence(state, details, null);
    }
  }, [location.pathname]);
}

/**
 * Manual presence update function
 * Use this when you need to update presence from specific game events
 */
export function updateDiscordPresence(state, details = null, showElapsedTime = false) {
  if (!isElectron || !window.electron || !window.electron.discord) {
    return;
  }

  const startTimestamp = showElapsedTime ? Math.floor(Date.now() / 1000) : null;
  window.electron.discord.updatePresence(state, details, startTimestamp);
}

/**
 * Clear Discord presence
 */
export function clearDiscordPresence() {
  if (!isElectron || !window.electron || !window.electron.discord) {
    return;
  }

  window.electron.discord.clearPresence();
}

export default useDiscordPresence;
